import { clsx } from "clsx";
import { ChevronDown, ChevronRight, ScrollText } from "lucide-react";
import { Fragment, useState } from "react";
import { UsersPagination } from "@/components/admin/UsersPagination";
import {
  Badge,
  Card,
  EmptyState,
  Input,
  Select,
  Spinner,
} from "@/components/common";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuditFilterOptions, useAuditLogs } from "@/hooks/useAudit";
import type {
  AuditCategory,
  AuditLogEntry,
  AuditLogFilters,
  FieldChange,
} from "@/types/audit";

const ACTION_LABELS: Record<string, string> = {
  GenerateTerminationPdf: "Wygenerowanie wypowiedzenia OC",
  Created: "Utworzenie",
  Updated: "Zmiana",
  Deleted: "Usunięcie",
  Login: "Logowanie",
  LoginFailed: "Nieudane logowanie",
  LoginBlocked: "Logowanie na zablokowane konto",
  MfaChallenge: "Wysłano kod logowania",
  MfaVerified: "Kod logowania potwierdzony",
  MfaFailed: "Błędny kod logowania",
  MfaCodeResent: "Ponowna wysyłka kodu",
  PasswordChanged: "Zmiana hasła",
  PasswordReset: "Reset hasła",
  PasswordResetRequested: "Prośba o reset hasła",
  TrustedDeviceRemoved: "Usunięto zaufane urządzenie",
  TrustedDevicesReset: "Reset zaufanych urządzeń",
  TotpEnabled: "Włączono aplikację 2FA",
  TotpDisabled: "Wyłączono aplikację 2FA",
};

const ENTITY_LABELS: Record<string, string> = {
  Client: "Klient",
  ConsentRequest: "Zgoda RODO",
  ConsentDocument: "Dokument zgody",
  Agent: "Agent",
  AgentAuthorization: "Upoważnienie agenta",
  AgentExam: "Egzamin agenta",
  Settlement: "Rozliczenie",
  SettlementRevision: "Korekta rozliczenia",
  ContractBonus: "Premia z kontraktu",
  AppRole: "Rola",
  User: "Konto użytkownika",
};

const CATEGORY_OPTIONS: { value: AuditCategory; label: string }[] = [
  { value: "", label: "Wszystkie zdarzenia" },
  { value: "data", label: "Zmiany danych" },
  { value: "auth", label: "Logowania i bezpieczeństwo" },
];

const ALL = "";

const timestampFormat = new Intl.DateTimeFormat("pl-PL", {
  dateStyle: "short",
  timeStyle: "medium",
});

function actionLabel(action: string) {
  return ACTION_LABELS[action] ?? action;
}

function entityLabel(entityType: string) {
  return ENTITY_LABELS[entityType] ?? entityType;
}

function actionVariant(action: string) {
  if (
    action === "Deleted" ||
    action.includes("Failed") ||
    action.includes("Blocked")
  ) {
    return "error" as const;
  }
  if (action === "Created" || action === "MfaVerified" || action === "Login") {
    return "success" as const;
  }
  if (action === "Updated") {
    return "info" as const;
  }
  return "default" as const;
}

function parseChanges(changes: string | null): [string, FieldChange][] {
  if (!changes) {
    return [];
  }
  try {
    return Object.entries(JSON.parse(changes) as Record<string, FieldChange>);
  } catch {
    return [];
  }
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? "—" : value.join(", ");
  }
  if (typeof value === "boolean") {
    return value ? "tak" : "nie";
  }
  return String(value);
}

/** "2026-11-01" from the date input → start of that day (UTC) for the API. */
function dayStart(date: string): string | undefined {
  return date ? new Date(`${date}T00:00:00`).toISOString() : undefined;
}

function dayEnd(date: string): string | undefined {
  if (!date) {
    return undefined;
  }
  const end = new Date(`${date}T00:00:00`);
  end.setDate(end.getDate() + 1);
  return end.toISOString();
}

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [category, setCategory] = useState<AuditCategory>("");
  const [userId, setUserId] = useState(ALL);
  const [entityType, setEntityType] = useState(ALL);
  const [action, setAction] = useState(ALL);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<number | null>(null);

  const filters: AuditLogFilters = {
    page,
    pageSize,
    category,
    userId: userId || undefined,
    entityType: entityType || undefined,
    action: action || undefined,
    from: dayStart(fromDate),
    to: dayEnd(toDate),
    search: search.trim() || undefined,
  };

  const { data, isLoading, isFetching } = useAuditLogs(filters);
  const { data: options } = useAuditFilterOptions();

  function update<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
      setExpanded(null);
    };
  }

  const userOptions = [
    { value: ALL, label: "Wszyscy użytkownicy" },
    { value: "system", label: "System" },
    ...(options?.users ?? []).map((u) => ({ value: u.userId, label: u.name })),
  ];
  const entityOptions = [
    { value: ALL, label: "Wszystkie obiekty" },
    ...(options?.entityTypes ?? []).map((e) => ({
      value: e,
      label: entityLabel(e),
    })),
  ];
  const actionOptions = [
    { value: ALL, label: "Wszystkie akcje" },
    ...(options?.actions ?? []).map((a) => ({
      value: a,
      label: actionLabel(a),
    })),
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dziennik zmian"
        description="Kto, kiedy i co zmienił w danych klientów, zgodach RODO, agentach, rozliczeniach i rolach — oraz wszystkie logowania. Wpisów nie można edytować ani usuwać."
      />

      <Card padding="sm">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Select
            value={category}
            onChange={update((v: string) => setCategory(v as AuditCategory))}
            options={CATEGORY_OPTIONS}
            size="sm"
            label="Rodzaj"
          />
          <Select
            value={userId}
            onChange={update(setUserId)}
            options={userOptions}
            size="sm"
            label="Kto"
          />
          <Select
            value={entityType}
            onChange={update(setEntityType)}
            options={entityOptions}
            size="sm"
            label="Obiekt"
          />
          <Select
            value={action}
            onChange={update(setAction)}
            options={actionOptions}
            size="sm"
            label="Akcja"
          />
          <Input
            type="date"
            label="Od"
            value={fromDate}
            onChange={(e) => update(setFromDate)(e.target.value)}
          />
          <Input
            type="date"
            label="Do"
            value={toDate}
            onChange={(e) => update(setToDate)(e.target.value)}
          />
          <div className="sm:col-span-2">
            <Input
              label="Szukaj"
              placeholder="Opis, identyfikator obiektu lub adres IP"
              value={search}
              onChange={(e) => update(setSearch)(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {isLoading || !data ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : data.items.length === 0 ? (
        <EmptyState
          icon={<ScrollText size={40} aria-hidden="true" />}
          title="Brak wpisów"
          description="Żadne zdarzenie nie pasuje do wybranych filtrów."
        />
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-edge-soft text-left text-xs font-medium text-dim">
                  <th className="w-8 px-3 py-3" />
                  <th className="px-3 py-3">Kiedy</th>
                  <th className="px-3 py-3">Kto</th>
                  <th className="px-3 py-3">Akcja</th>
                  <th className="px-3 py-3">Obiekt</th>
                  <th className="px-3 py-3">Szczegóły</th>
                  <th className="px-3 py-3">IP</th>
                </tr>
              </thead>
              <tbody className={clsx(isFetching && "opacity-60")}>
                {data.items.map((entry) => (
                  <AuditRow
                    key={entry.id}
                    entry={entry}
                    isExpanded={expanded === entry.id}
                    onToggle={() =>
                      setExpanded(expanded === entry.id ? null : entry.id)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-edge-soft px-4 py-3">
            <UsersPagination
              page={data.page}
              pageSize={data.pageSize}
              totalCount={data.totalCount}
              totalPages={data.totalPages}
              onPageChange={(p) => {
                setPage(p);
                setExpanded(null);
              }}
              onPageSizeChange={update(setPageSize)}
            />
          </div>
        </Card>
      )}
    </div>
  );
}

function AuditRow({
  entry,
  isExpanded,
  onToggle,
}: {
  entry: AuditLogEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const changes = parseChanges(entry.changes);
  const canExpand = changes.length > 0;

  return (
    <Fragment>
      <tr
        className={clsx(
          "border-b border-edge-soft align-top last:border-0",
          canExpand && "cursor-pointer hover:bg-subtle",
        )}
        onClick={canExpand ? onToggle : undefined}
      >
        <td className="px-3 py-3 text-faint">
          {canExpand &&
            (isExpanded ? (
              <ChevronDown size={16} aria-label="Zwiń" />
            ) : (
              <ChevronRight size={16} aria-label="Pokaż zmiany" />
            ))}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-sub">
          {timestampFormat.format(new Date(entry.timestamp))}
        </td>
        <td className="px-3 py-3 whitespace-nowrap text-main">
          {entry.userName}
        </td>
        <td className="px-3 py-3 whitespace-nowrap">
          <Badge variant={actionVariant(entry.action)}>
            {actionLabel(entry.action)}
          </Badge>
        </td>
        <td className="px-3 py-3">
          <div className="text-main">{entityLabel(entry.entityType)}</div>
          {entry.entityId && (
            <div className="max-w-[180px] truncate font-mono text-xs text-faint">
              {entry.entityId}
            </div>
          )}
        </td>
        <td className="px-3 py-3 text-sub">
          <p
            className="line-clamp-2 max-w-[260px] break-words"
            title={entry.details ?? undefined}
          >
            {canExpand
              ? `Pola: ${changes.map(([field]) => field).join(", ")}`
              : (entry.details ?? "—")}
          </p>
        </td>
        <td className="px-3 py-3 font-mono text-xs whitespace-nowrap text-dim">
          {entry.ipAddress}
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-b border-edge-soft bg-subtle">
          <td />
          <td colSpan={6} className="px-3 py-3">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-dim">
                  <th className="w-1/4 py-1 pr-3 font-medium">Pole</th>
                  <th className="w-3/8 py-1 pr-3 font-medium">Było</th>
                  <th className="w-3/8 py-1 font-medium">Jest</th>
                </tr>
              </thead>
              <tbody>
                {changes.map(([field, change]) => (
                  <tr key={field} className="align-top">
                    <td className="py-1 pr-3 font-medium text-sub">{field}</td>
                    <td className="py-1 pr-3 break-all text-red-700 dark:text-red-400">
                      {formatValue(change.old)}
                    </td>
                    <td className="py-1 break-all text-green-700 dark:text-green-400">
                      {formatValue(change.new)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </Fragment>
  );
}
